<template>
  <div class="tb-unsaved-changes-shell">
  <Teleport to="body">
    <Transition name="tb-floating-save">
      <div
        v-if="showFloating"
        class="pointer-events-none fixed bottom-6 right-6 z-[110] flex max-w-[min(100vw-2rem,20rem)] flex-col items-end gap-2"
        role="status"
        :aria-label="t('unsavedChanges.floatingTitle')"
      >
        <div
          class="pointer-events-auto flex w-full flex-col gap-3 rounded-2xl border border-gray-200/90 bg-white/95 px-4 py-3.5 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/95"
        >
          <p class="text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">
            {{ t('unsavedChanges.floatingTitle') }}
          </p>
          <div class="flex flex-wrap justify-end gap-2">
            <BaseButton type="button" variant="outline" size="sm" :disabled="floatingSaving" @click="onDiscard">
              {{ t('unsavedChanges.discard') }}
            </BaseButton>
            <BaseButton type="button" variant="primary" size="sm" :loading="floatingSaving" @click="onSave">
              {{ t('unsavedChanges.save') }}
            </BaseButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <BaseModal
    :model-value="leaveDialogOpen"
    :title="t('unsavedChanges.title')"
    :subtitle="t('unsavedChanges.message')"
    size="md"
    :closable="true"
    :close-on-backdrop="false"
    @update:model-value="onLeaveDialogOpenChange"
  >
    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <BaseButton type="button" variant="outline" @click="onLeaveCancel">
          {{ t('unsavedChanges.cancel') }}
        </BaseButton>
        <BaseButton type="button" variant="outline" @click="onLeaveDiscard">
          {{ t('unsavedChanges.discard') }}
        </BaseButton>
        <BaseButton type="button" variant="primary" :loading="floatingSaving" @click="onLeaveSave">
          {{ t('unsavedChanges.save') }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import { useActionOverlayStore } from '@/stores/action-overlay'
import { useUnsavedChangesStore } from '@/stores/unsaved-changes'

const { t } = useI18n()
const store = useUnsavedChangesStore()
const actionOverlay = useActionOverlayStore()

const leaveDialogOpen = computed(() => store.leaveDialogOpen)

const floating = computed(() => store.floatingActive)

const showFloating = computed(() => {
  const f = floating.value
  if (!f) return false
  return unref(f.isDirty)
})

const floatingSaving = computed(() => {
  const f = floating.value
  if (!f) return false
  return unref(f.saving)
})

function onLeaveDialogOpenChange(open: boolean): void {
  if (!open) {
    store.settleLeave('cancel')
  }
}

function onLeaveCancel(): void {
  store.settleLeave('cancel')
}

async function onLeaveDiscard(): Promise<void> {
  try {
    await Promise.resolve(floating.value?.discard())
  } finally {
    store.settleLeave('discard')
  }
}

async function onLeaveSave(): Promise<void> {
  const f = floating.value
  if (!f) {
    store.settleLeave('save')
    return
  }
  actionOverlay.showLoading(t('unsavedChanges.overlaySavingTitle'), t('unsavedChanges.overlaySavingMessage'))
  try {
    await f.save()
  } catch {
    actionOverlay.hide()
    return
  }
  if (unref(f.isDirty)) {
    actionOverlay.hide()
    return
  }
  await actionOverlay.showSuccess(t('unsavedChanges.overlaySuccessTitle'), t('unsavedChanges.overlaySuccessMessage'))
  store.settleLeave('save')
}

function onDiscard(): void {
  void Promise.resolve(floating.value?.discard())
}

async function onSave(): Promise<void> {
  const f = floating.value
  if (!f) return
  actionOverlay.showLoading(t('unsavedChanges.overlaySavingTitle'), t('unsavedChanges.overlaySavingMessage'))
  try {
    await f.save()
  } catch {
    actionOverlay.hide()
    return
  }
  if (unref(f.isDirty)) {
    actionOverlay.hide()
    return
  }
  await actionOverlay.showSuccess(t('unsavedChanges.overlaySuccessTitle'), t('unsavedChanges.overlaySuccessMessage'))
}
</script>

<style scoped>
.tb-unsaved-changes-shell {
  display: contents;
}

.tb-floating-save-enter-active,
.tb-floating-save-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.tb-floating-save-enter-from,
.tb-floating-save-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
